import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { Spinner } from '../../components/ui/Spinner'

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  })

type FormData = z.infer<typeof schema>

export default function ForcePasswordChangePage() {
  const navigate = useNavigate()
  const { user, setForcePasswordChange } = useAuthStore()
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(values: FormData) {
    try {
      const { error: pwdErr } = await supabase.auth.updateUser({ password: values.password })
      if (pwdErr) {
        toast.error(pwdErr.message)
        return
      }

      const { error: metaErr } = await supabase.auth.updateUser({
        data: { force_password_change: false },
      })
      if (metaErr) {
        toast.error('Password updated, but flag could not be cleared: ' + metaErr.message)
        return
      }

      setForcePasswordChange(false)
      toast.success('Password updated. Welcome to Palstro!')
      navigate('/dashboard', { replace: true })
    } catch (e) {
      toast.error((e as Error).message ?? 'Could not update password')
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    setForcePasswordChange(false)
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-amber-50 to-orange-100 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-card-hover p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-brand-100 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Set a new password</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {user?.email ? `Signed in as ${user.email}` : 'Required before continuing'}
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Your account was set up by an admin. Choose a new password that only you know — you&apos;ll
          use it the next time you sign in.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                autoFocus
                className="input pr-10"
                placeholder="At least 8 characters"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="label">Confirm new password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                className="input pr-10"
                placeholder="Repeat password"
                {...register('confirm')}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirm && (
              <p className="mt-1 text-xs text-red-500">{errors.confirm.message}</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full h-11">
            {isSubmitting ? <Spinner size="sm" /> : 'Save & Continue'}
          </button>
        </form>

        <button
          type="button"
          onClick={handleSignOut}
          className="mt-4 w-full text-xs text-gray-500 hover:text-gray-700"
        >
          Sign out instead
        </button>
      </div>
    </div>
  )
}
